import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stepper,
  Step,
  StepLabel,
  StepContent,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  CheckCircle,
  Lock as LockIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Business as BusinessIcon,
  SmartToy as SmartToyIcon,
} from '@mui/icons-material';
import TermsCheckbox from '../../components/Auth/TermsCheckbox';
import { API_CONFIG } from '../../config/apiConfig';

interface InvitationDetails {
  email: string;
  organizationName: string;
  inviterName: string;
  role: string;
}

const AcceptInvitation: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [invitationDetails, setInvitationDetails] = useState<InvitationDetails | null>(null);

  // Form fields
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [termsVersion] = useState('1.0.0');

  // Agent initialization progress
  const [initializingAgents, setInitializingAgents] = useState(false);
  const [agentInitProgress, setAgentInitProgress] = useState<string[]>([]);

  const steps = [
    'Verify Invitation',
    'Set Up Your Account',
    'Initialize AI Agents',
    'Ready to Start',
  ];

  const verifyInvitation = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Get API URL from environment or use default
      const apiUrl = API_CONFIG.USER_SERVICE_URL;
      const response = await fetch(`${apiUrl}/api/v1/invitations/verify?token=${token}`);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error?.message || 'Invalid or expired invitation');
      }

      const data = await response.json();
      setInvitationDetails(data.data);
      setActiveStep(1);
    } catch (err: any) {
      setError(err.message || 'Failed to verify invitation');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!token) {
      setError('Invalid invitation link. Please check your email for the correct link.');
      setLoading(false);
      return;
    }

    verifyInvitation();
  }, [token, verifyInvitation]);

  const validatePassword = (): boolean => {
    if (!firstName.trim() || !lastName.trim()) {
      setError('Please enter your first and last name');
      return false;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      return false;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return false;
    }

    // Check for common weak passwords
    const weakPasswords = ['password', '12345678', 'password123', 'admin123'];
    if (weakPasswords.includes(password.toLowerCase())) {
      setError('This password is too common. Please choose a stronger password.');
      return false;
    }

    if (!acceptTerms) {
      setError('You must accept the terms and conditions to continue');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validatePassword()) {
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      // Get API URL from environment or use default
      const apiUrl = API_CONFIG.USER_SERVICE_URL;

      // Accept invitation and create user account
      const response = await fetch(`${apiUrl}/api/v1/invitations/accept`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token,
          firstName,
          lastName,
          password,
          acceptTerms,
          termsVersion,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error?.message || 'Failed to create account');
      }

      const data = await response.json();

      // Move to agent initialization step
      setActiveStep(2);
      await initializeAgents(data.data.userId, data.data.accessToken);

    } catch (err: any) {
      setError(err.message || 'Failed to create account');
      setSubmitting(false);
    }
  };

  const initializeAgents = async (userId: string, accessToken: string) => {
    try {
      setInitializingAgents(true);
      setAgentInitProgress([]);

      // Get API URL from environment or use default
      const apiUrl = API_CONFIG.USER_SERVICE_URL;

      // List of default agents to initialize
      const defaultAgents = [
        { name: 'Alex', description: 'General AI assistant' },
        { name: 'Maya', description: 'Context-aware productivity assistant' },
        { name: 'Nova', description: 'Knowledge management specialist' },
        { name: 'Atlas', description: 'Research and analysis expert' },
        { name: 'Nexus', description: 'Integration and workflow coordinator' },
      ];

      for (const agent of defaultAgents) {
        setAgentInitProgress(prev => [...prev, `Initializing ${agent.name} - ${agent.description}...`]);

        // Initialize agent via API
        await fetch(`${apiUrl}/api/v1/agents/initialize`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            userId,
            agentName: agent.name,
          }),
        });

        // Add a small delay for visual feedback
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      setAgentInitProgress(prev => [...prev, '✅ All agents initialized successfully!']);

      // Move to final step
      setTimeout(() => {
        setActiveStep(3);
        setInitializingAgents(false);

        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login', {
            state: {
              message: 'Your account has been created successfully! Please log in with your new credentials.',
              email: invitationDetails?.email
            }
          });
        }, 3000);
      }, 2000);

    } catch (err: any) {
      setError('Failed to initialize agents, but your account was created. You can set up agents later.');
      setInitializingAgents(false);

      // Still redirect to login even if agent init fails
      setTimeout(() => {
        navigate('/login', {
          state: {
            message: 'Your account has been created. Please log in to continue.',
            email: invitationDetails?.email
          }
        });
      }, 5000);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="sm">
        <div style={{ marginTop: 64, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <CircularProgress />
          <Typography variant="h6" style={{ marginTop: 16 }}>
            Verifying your invitation...
          </Typography>
        </div>
      </Container>
    );
  }

  // Render different layouts based on the active step
  if (activeStep === 1 && invitationDetails) {
    // Step 1: Account Setup with sidebar layout
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0C1319 0%, #1a2332 100%)',
        display: 'flex',
        alignItems: 'center',
        paddingTop: 32,
        paddingBottom: 32,
      }}>
        <Container maxWidth="lg">
          <div style={{ marginBottom: 32 }}>
            <Typography
              component="h1"
              variant="h3"
              align="center"
              style={{
                color: 'white',
                fontWeight: 'bold',
                marginBottom: 8,
              }}
            >
              Welcome to Prismatic Forge
            </Typography>
            <Typography
              variant="h6"
              align="center"
              style={{
                color: 'rgba(255, 255, 255, 0.7)',
                fontWeight: 'normal',
              }}
            >
              Complete your account setup to get started
            </Typography>
          </div>

          <div style={{ display: 'flex', gap: 24, alignItems: 'stretch' }}>
            {/* Left Sidebar - Invitation Details */}
            <Paper
              elevation={6}
              style={{
                width: '350px',
                padding: 32,
                background: 'rgba(26, 35, 50, 0.95)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(77, 196, 210, 0.2)',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Typography
                variant="h6"
                style={{
                  marginBottom: 24,
                  color: '#4DC4D2',
                  fontWeight: 'bold',
                }}
              >
                Invitation Details
              </Typography>

              <div style={{ marginBottom: 24 }}>
                <Typography
                  variant="body2"
                  style={{
                    marginBottom: 16,
                    color: 'rgba(255, 255, 255, 0.7)',
                    lineHeight: 1.6,
                  }}
                >
                  You've been invited by <strong style={{ color: '#4DC4D2' }}>{invitationDetails.inviterName}</strong> to join
                  the organization as a team member.
                </Typography>
              </div>

              <List style={{ flex: 1 }}>
                <ListItem style={{ paddingLeft: 0, paddingRight: 0, paddingTop: 16, paddingBottom: 16 }}>
                  <ListItemIcon>
                    <EmailIcon style={{ color: '#4DC4D2' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="overline" style={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                        Email Address
                      </Typography>
                    }
                    secondary={
                      <Typography variant="body1" style={{ fontWeight: 500, color: 'white' }}>
                        {invitationDetails.email}
                      </Typography>
                    }
                  />
                </ListItem>

                <ListItem style={{ paddingLeft: 0, paddingRight: 0, paddingTop: 16, paddingBottom: 16 }}>
                  <ListItemIcon>
                    <BusinessIcon style={{ color: '#325BA8' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="overline" style={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                        Organization
                      </Typography>
                    }
                    secondary={
                      <Typography variant="body1" style={{ fontWeight: 500, color: 'white' }}>
                        {invitationDetails.organizationName}
                      </Typography>
                    }
                  />
                </ListItem>

                <ListItem style={{ paddingLeft: 0, paddingRight: 0, paddingTop: 16, paddingBottom: 16 }}>
                  <ListItemIcon>
                    <PersonIcon style={{ color: '#892987' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="overline" style={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                        Your Role
                      </Typography>
                    }
                    secondary={
                      <Typography
                        variant="body1"
                        style={{
                          fontWeight: 500,
                          color: 'white',
                          textTransform: 'capitalize',
                        }}
                      >
                        {invitationDetails.role}
                      </Typography>
                    }
                  />
                </ListItem>
              </List>

              <Alert
                severity="info"
                style={{
                  marginTop: 'auto',
                  backgroundColor: 'rgba(77, 196, 210, 0.15)',
                  border: '1px solid rgba(77, 196, 210, 0.3)',
                  color: 'rgba(255, 255, 255, 0.9)',
                }}
              >
                <Typography variant="caption" style={{ color: 'inherit' }}>
                  After creating your account, you'll get access to AI-powered productivity tools
                  and collaborative workspaces.
                </Typography>
              </Alert>
            </Paper>

            {/* Center/Right - Signup Form */}
            <Paper
              elevation={6}
              style={{
                flex: 1,
                padding: 40,
                background: 'rgba(26, 35, 50, 0.95)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(77, 196, 210, 0.2)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <div style={{ width: '100%', maxWidth: '500px' }}>
                <Typography
                  variant="h5"
                  style={{
                    marginBottom: 8,
                    fontWeight: 'bold',
                    color: '#4DC4D2',
                  }}
                >
                  Create Your Account
                </Typography>
                <Typography
                  variant="body2"
                  style={{
                    marginBottom: 32,
                    color: 'rgba(255, 255, 255, 0.7)',
                  }}
                >
                  Set up your personal account details to continue
                </Typography>

                <div>
                  <form onSubmit={handleSubmit}>
                    <div style={{ display: 'flex', gap: 16, marginBottom: 24 }}>
                      <TextField
                        required
                        fullWidth
                        id="firstName"
                        label="First Name"
                        name="firstName"
                        autoComplete="given-name"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        disabled={submitting}
                        variant="outlined"
                      />
                      <TextField
                        required
                        fullWidth
                        id="lastName"
                        label="Last Name"
                        name="lastName"
                        autoComplete="family-name"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        disabled={submitting}
                        variant="outlined"
                      />
                    </div>

                    <TextField
                      required
                      fullWidth
                      name="password"
                      label="Password"
                      type={showPassword ? 'text' : 'password'}
                      id="password"
                      autoComplete="new-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      disabled={submitting}
                      variant="outlined"
                      style={{ marginBottom: 24 }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LockIcon style={{ color: 'rgba(255, 255, 255, 0.5)' }} />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowPassword(!showPassword)}
                              edge="end"
                              disabled={submitting}
                              style={{ color: 'rgba(255, 255, 255, 0.5)' }}
                            >
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      helperText="At least 8 characters"
                    />

                    <TextField
                      required
                      fullWidth
                      name="confirmPassword"
                      label="Confirm Password"
                      type={showConfirmPassword ? 'text' : 'password'}
                      id="confirmPassword"
                      autoComplete="new-password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      disabled={submitting}
                      variant="outlined"
                      style={{ marginBottom: 32 }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LockIcon style={{ color: 'rgba(255, 255, 255, 0.5)' }} />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                              edge="end"
                              disabled={submitting}
                              style={{ color: 'rgba(255, 255, 255, 0.5)' }}
                            >
                              {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />

                    {/* Terms and Conditions */}
                    <div style={{ marginBottom: 24 }}>
                      <TermsCheckbox
                        checked={acceptTerms}
                        onChange={setAcceptTerms}
                        variant="invitation"
                      />
                    </div>

                    {error && activeStep === 1 && (
                      <Alert severity="error" style={{ marginBottom: 24 }}>
                        {error}
                      </Alert>
                    )}

                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      size="large"
                      disabled={submitting}
                      style={{
                        paddingTop: 12,
                        paddingBottom: 12,
                        backgroundColor: '#325BA8',
                        fontSize: '1.1rem',
                        fontWeight: 'bold',
                        textTransform: 'none',
                      }}
                    >
                      {submitting ? (
                        <>
                          <CircularProgress size={24} style={{ marginRight: 8, color: 'white' }} />
                          Creating Account...
                        </>
                      ) : (
                        'Create Account'
                      )}
                    </Button>
                  </form>
                </div>
              </div>
            </Paper>
          </div>
        </Container>
      </div>
    );
  }

  // Default stepper layout for other steps
  return (
    <Container component="main" maxWidth="md">
      <div style={{ marginTop: 32, marginBottom: 32 }}>
        <Paper elevation={3} style={{ padding: 32 }}>
          <Typography component="h1" variant="h4" align="center" style={{ marginBottom: 32 }}>
            Welcome to Prismatic Forge
          </Typography>

          <Stepper activeStep={activeStep} orientation="vertical">
            <Step>
              <StepLabel
                error={!!error && activeStep === 0}
              >
                {steps[0]}
              </StepLabel>
              <StepContent>
                {error && activeStep === 0 ? (
                  <Alert severity="error" style={{ marginTop: 16 }}>
                    {error}
                  </Alert>
                ) : (
                  <Alert severity="success" style={{ marginTop: 16 }}>
                    Invitation verified successfully!
                  </Alert>
                )}
              </StepContent>
            </Step>

            <Step>
              <StepLabel>{steps[1]}</StepLabel>
              <StepContent>
                {/* This step is now rendered with the custom layout above */}
              </StepContent>
            </Step>

            <Step>
              <StepLabel>{steps[2]}</StepLabel>
              <StepContent>
                <div style={{ marginTop: 16 }}>
                  <Typography variant="body1" style={{ marginBottom: 16 }}>
                    Setting up your AI agents...
                  </Typography>

                  <List dense>
                    {agentInitProgress.map((message, index) => (
                      <ListItem key={index}>
                        <ListItemIcon>
                          {message.startsWith('✅') ? (
                            <CheckCircle color="success" />
                          ) : (
                            <SmartToyIcon color="primary" />
                          )}
                        </ListItemIcon>
                        <ListItemText primary={message} />
                      </ListItem>
                    ))}
                  </List>

                  {initializingAgents && (
                    <div style={{ display: 'flex', justifyContent: 'center', marginTop: 24 }}>
                      <CircularProgress />
                    </div>
                  )}
                </div>
              </StepContent>
            </Step>

            <Step>
              <StepLabel>{steps[3]}</StepLabel>
              <StepContent>
                <Alert severity="success" style={{ marginTop: 16, marginBottom: 16 }}>
                  <Typography variant="h6">All set!</Typography>
                  <Typography variant="body2">
                    Your account has been created and your AI agents are ready.
                    Redirecting you to the login page...
                  </Typography>
                </Alert>
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: 24 }}>
                  <CircularProgress />
                </div>
              </StepContent>
            </Step>
          </Stepper>
        </Paper>
      </div>
    </Container>
  );
};

export default AcceptInvitation;
